import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CostCategory,
  CreateCostCategoryInput,
  CreateEmployeeInput,
  CreateProjectInput,
  CreateUserInput,
  CreateVendorInput,
  Employee,
  Project,
  UpdateCostCategoryInput,
  UpdateEmployeeInput,
  UpdateProjectInput,
  UpdateUserInput,
  UpdateVendorInput,
  User,
  Vendor,
} from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

/** Dictionaries change rarely; caching them keeps the assign screen instant. */
const DICTIONARY_STALE_TIME = 5 * 60 * 1000;

/* ─────────────────────────────── Queries ───────────────────────────────── */

export function useProjects(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.projects.all, { includeInactive }],
    queryFn: () => apiRequest<Project[]>('/projects', { query: { includeInactive } }),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

export function useVendors(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.vendors.all, { includeInactive }],
    queryFn: () => apiRequest<Vendor[]>('/vendors', { query: { includeInactive } }),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

/** `includeInactive` is only for the settings tab — pickers must not offer hidden rows. */
export function useCostCategories(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.categories.all, { includeInactive }],
    queryFn: () => apiRequest<CostCategory[]>('/cost-categories', { query: { includeInactive } }),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

export function useEmployees(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.employees.all, { includeInactive }],
    queryFn: () => apiRequest<Employee[]>('/employees', { query: { includeInactive } }),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users.all,
    queryFn: () => apiRequest<User[]>('/users'),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

/** Office only — `/users` answers 403 to a site manager, so callers gate it. */
export function useSiteManagers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.users.byRole('SITE_MANAGER'),
    queryFn: () => apiRequest<User[]>('/users', { query: { role: 'SITE_MANAGER' } }),
    staleTime: DICTIONARY_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}

/* ────────────────────────────── Mutations ──────────────────────────────── */

/**
 * A dictionary edit ripples into every screen that renders a project or vendor
 * name, so each mutation clears the whole dictionary group plus the invoice
 * lists that embed those names.
 */
function useInvalidateDictionary(key: readonly unknown[]) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
  };
}

export function useCreateProject() {
  const invalidate = useInvalidateDictionary(queryKeys.projects.all);

  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      apiRequest<Project>('/projects', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateProject() {
  const invalidate = useInvalidateDictionary(queryKeys.projects.all);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProjectInput }) =>
      apiRequest<Project>(`/projects/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreateVendor() {
  const invalidate = useInvalidateDictionary(queryKeys.vendors.all);

  return useMutation({
    mutationFn: (input: CreateVendorInput) =>
      apiRequest<Vendor>('/vendors', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateVendor() {
  const invalidate = useInvalidateDictionary(queryKeys.vendors.all);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateVendorInput }) =>
      apiRequest<Vendor>(`/vendors/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreateCostCategory() {
  const invalidate = useInvalidateDictionary(queryKeys.categories.all);

  return useMutation({
    mutationFn: (input: CreateCostCategoryInput) =>
      apiRequest<CostCategory>('/cost-categories', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateCostCategory() {
  const invalidate = useInvalidateDictionary(queryKeys.categories.all);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCostCategoryInput }) =>
      apiRequest<CostCategory>(`/cost-categories/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreateEmployee() {
  const invalidate = useInvalidateDictionary(queryKeys.employees.all);

  return useMutation({
    mutationFn: (input: CreateEmployeeInput) =>
      apiRequest<Employee>('/employees', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateEmployee() {
  const invalidate = useInvalidateDictionary(queryKeys.employees.all);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEmployeeInput }) =>
      apiRequest<Employee>(`/employees/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreateUser() {
  const invalidate = useInvalidateDictionary(queryKeys.users.all);

  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      apiRequest<User>('/users', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateDictionary(queryKeys.users.all);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      apiRequest<User>(`/users/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}
